import {
  Button,
  ButtonGroup,
  HStack,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { RiCodeSSlashLine } from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { useNavigate, useResolvedPath } from "react-router";

import { SidebarToggleButton } from "@/common/components/CommonIconButtons";
import { CopyText } from "@/common/components/CopyInput";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import HeaderLayout from "@/common/components/HeaderLayout";
import MotionBox from "@/common/components/MotionBox";
import { MotionFade } from "@/common/components/MotionComponents";
import RouterTabs, {
  Route,
  useActivePathRouteIndex,
} from "@/common/components/RouterTabs";
import { useSlugParam, useSubsegmentParam } from "@/common/hooks/useParam";
import { FeatureIndicatorsDisplay } from "@/feature/components/FeatureDisplay";
import { FeatureTreeNavigation } from "@/feature/components/FeatureTreeNavigation";
import { useSetupInstructions } from "@/feature/components/SetupInstructions";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useFlag } from "@reflag/react-sdk";

type FeatureTabRoute = Route<
  | {
      id: "access" | "optIn" | "config" | "monitor" | "settings";
      hasSidebar?: boolean;
      hasCodeInstructions?: boolean;
    }
  | {
      id: "analyze" | "targeting" | "settings";
      hasSidebar?: boolean;
      hasCodeInstructions?: boolean;
    }
>;

export default function Feature() {
  const { featureId } = useSlugParam("feature");
  const navigate = useNavigate();
  const toast = useToast();
  const [subsegment] = useSubsegmentParam();
  const params = new URLSearchParams();
  if (subsegment.length) {
    subsegment.forEach((subsegment) => params.append("subsegment", subsegment));
  }
  const search = params.toString();

  const { data: featureNames } = useFeatureNamesData();
  const featureName = featureNames?.find((f) => f.id === featureId);

  const { isEnabled: hasDynamicConfigs } = useFlag("dynamic-configs");
  const { isEnabled: showEndUserOptInUi } = useFlag("end-user-opt-in-ui");

  const { data: feature, isLoading, isError } = useFeatureData(featureId);
  useEffect(() => {
    if (isError) {
      navigate("..");
      toast({
        title: "Could not retrieve flag",
        status: "error",
      });
    }
  }, [isError, navigate, toast]);

  // Resolve all paths at the top level to follow Rules of Hooks
  const accessPath = useResolvedPath("access");
  const optInPath = useResolvedPath("opt-in");
  const monitorPath = useResolvedPath("monitor");
  const settingsPath = useResolvedPath("settings");

  const tabsRoutes: FeatureTabRoute[] = [
    {
      id: "access",
      route: { ...accessPath, search },
      name: "Access",
      hasSidebar: true,
      hasCodeInstructions: true,
    },
    ...(showEndUserOptInUi && feature?.optInEnabled
      ? [
          {
            id: "optIn" as const,
            route: { ...optInPath, search },
            name: "Opt-ins",
            hasSidebar: true,
          },
        ]
      : []),
    {
      id: "monitor",
      route: { ...monitorPath, search },
      name: "Monitor",
      hasSidebar: true,
      hasCodeInstructions: true,
    },
    {
      id: "settings",
      route: { ...settingsPath, search },
      name: "Settings",
    },
  ];

  const configPath = useResolvedPath("config");
  if (hasDynamicConfigs) {
    tabsRoutes.splice(1, 0, {
      id: "config",
      route: { ...configPath, search },
      name: "Remote config",
      hasSidebar: true,
      hasCodeInstructions: true,
    });
  }

  const activeRouteIndex = useActivePathRouteIndex(tabsRoutes);
  const activeTab = tabsRoutes[activeRouteIndex];

  return (
    <>
      <VisuallyHidden>
        <h1>{feature?.name}</h1>
      </VisuallyHidden>
      <HeaderLayout
        title={
          featureName && (
            <HStack ml={-3} spacing={4}>
              <FeatureTreeNavigation rootFeatureId={featureId} />
              <AnimatePresence initial={false}>
                {isLoading || !feature ? (
                  <DelayedSpinner size="xs" variant="subtle" />
                ) : (
                  <MotionFade alignItems="center" display="flex" gap={4}>
                    <FeatureIndicatorsDisplay
                      feature={feature}
                      iconSize="md"
                      autoFeedbackSurveyLink
                      showAutoFeedbackSurveyIndicator
                      showStaleIndicator
                    />
                    <CopyText
                      fontFamily="code"
                      fontSize="sm"
                      value={feature.key}
                    />
                  </MotionFade>
                )}
              </AnimatePresence>
            </HStack>
          )
        }
        borderless
      >
        <RouterTabs
          activeRouteIndex={activeRouteIndex}
          isLoading={isLoading}
          rightActions={
            <ButtonGroup spacing={1}>
              <ShowInstructionsButton currentTab={activeTab} />
              {activeTab?.hasSidebar && (
                <SidebarToggleButton sidebarKey={featureId} />
              )}
            </ButtonGroup>
          }
          routes={tabsRoutes}
          tabListProps={{ px: 3 }}
          titleTemplate={`Flag › ${feature?.name} › %s`}
        />
      </HeaderLayout>
    </>
  );
}

function ShowInstructionsButton({
  currentTab,
}: {
  currentTab?: FeatureTabRoute;
}) {
  const [isVisible, setIsVisible] = useSetupInstructions();

  const showOnTab = currentTab?.hasCodeInstructions;

  return (
    showOnTab && (
      <AnimatePresence initial={false}>
        {!isVisible && (
          <MotionBox
            key="show-instructions"
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.5rem" }}
            initial={{ opacity: 0, translateY: "0.5rem" }}
            transition={{ duration: "0.1", ease: "easeInOut" }}
            layoutScroll
          >
            <Button
              leftIcon={<RiCodeSSlashLine size={16} />}
              variant="ghostDimmed"
              onClick={() => setIsVisible(true)}
            >
              Show instructions
            </Button>
          </MotionBox>
        )}
      </AnimatePresence>
    )
  );
}
